import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../contexts/UserContext';
import { CalendarCheck, ChevronLeft, ChevronRight, MapPin, Car, Plane, Home, Umbrella, Plus, X, Users } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://ventro.energy';

const PlanningWork = () => {
  const { user } = useContext(UserContext);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [assignments, setAssignments] = useState([]);
  const [travels, setTravels] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publishedDateRange, setPublishedDateRange] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [projectDetails, setProjectDetails] = useState(null);
  const [loadingProjectDetails, setLoadingProjectDetails] = useState(false);
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [holidayForm, setHolidayForm] = useState({
    start_date: '',
    end_date: '',
    notes: ''
  });
  const [savingHoliday, setSavingHoliday] = useState(false);

  // Personal work log states
  const [personalLogs, setPersonalLogs] = useState([]);
  const [showPersonalLogModal, setShowPersonalLogModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [personalLogForm, setPersonalLogForm] = useState({
    date: '',
    project_name: '',
    hours_from: '',
    hours_to: '',
    notes: ''
  });
  const [savingPersonalLog, setSavingPersonalLog] = useState(false);

  const fetchTravels = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_BASE_URL}/api/travel/?page_size=1000`, { headers });

      const travelsData = Array.isArray(response.data?.results)
        ? response.data.results
        : (Array.isArray(response.data) ? response.data : []);

      // Filter travels for current user with sent status (email_sent=true)
      const userTravels = travelsData.filter(travel =>
        travel.user_id === user?.id && travel.email_sent === true
      );

      setTravels(userTravels);
    } catch (error) {
      console.error('Error fetching travels:', error);
    }
  };

  const fetchHolidays = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_BASE_URL}/api/holidays/?user_id=${user?.id}`, { headers });

      const holidaysData = Array.isArray(response.data?.results)
        ? response.data.results
        : (Array.isArray(response.data) ? response.data : []);

      setHolidays(holidaysData);
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  };

  const fetchPersonalLogs = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_BASE_URL}/api/personal-work-logs/`, { headers });

      const logsData = Array.isArray(response.data?.results)
        ? response.data.results
        : (Array.isArray(response.data) ? response.data : []);

      setPersonalLogs(logsData);
    } catch (error) {
      console.error('Error fetching personal logs:', error);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchPublishedPlans();
      fetchTravels();
      fetchHolidays();
      fetchPersonalLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const fetchPublishedPlans = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_BASE_URL}/api/resource-plan-publish-history/`, { headers });

      if (response.data && response.data.length > 0) {
        // Get the most recent publication
        const latestPublication = response.data[0];
        setPublishedDateRange({
          start: latestPublication.start_date,
          end: latestPublication.end_date
        });

        // Filter assignments for current user
        const userAssignments = latestPublication.published_data?.assignments?.filter(
          assignment => assignment.user_id === user?.id
        ) || [];

        setAssignments(userAssignments);
      }
    } catch (error) {
      console.error('Error fetching published plans:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calendar navigation
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get calendar data
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  // Check if date has assignment or travel
  const getItemsForDate = (date) => {
    const items = [];

    // Normalize date to midnight for accurate comparison
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);

    // Add assignments (only within published date range)
    if (publishedDateRange) {
      const publishStart = new Date(publishedDateRange.start);
      publishStart.setHours(0, 0, 0, 0);

      const publishEnd = new Date(publishedDateRange.end);
      publishEnd.setHours(0, 0, 0, 0);

      // Check if date is within published range
      if (checkDate >= publishStart && checkDate <= publishEnd) {
        const dateAssignments = assignments.filter(assignment => {
          const startDate = new Date(assignment.start_date);
          startDate.setHours(0, 0, 0, 0);

          const endDate = new Date(assignment.end_date);
          endDate.setHours(0, 0, 0, 0);

          return checkDate >= startDate && checkDate <= endDate;
        });

        items.push(...dateAssignments.map(a => ({ ...a, type: 'assignment' })));
      }
    }

    // Add travels (no date range restriction for travels)
    const dateTravels = travels.filter(travel => {
      const travelDate = new Date(travel.start_date);
      travelDate.setHours(0, 0, 0, 0);

      return travelDate.getTime() === checkDate.getTime();
    });

    items.push(...dateTravels.map(t => ({ ...t, type: 'travel' })));

    // Add holidays
    const dateHolidays = holidays.filter(holiday => {
      const startDate = new Date(holiday.start_date);
      startDate.setHours(0, 0, 0, 0);

      const endDate = new Date(holiday.end_date);
      endDate.setHours(0, 0, 0, 0);

      return checkDate >= startDate && checkDate <= endDate;
    });

    items.push(...dateHolidays.map(h => ({ ...h, type: 'holiday' })));

    // Add personal work logs
    const datePersonalLogs = personalLogs.filter(log => {
      const logDate = new Date(log.date);
      logDate.setHours(0, 0, 0, 0);

      return logDate.getTime() === checkDate.getTime();
    });

    items.push(...datePersonalLogs.map(log => ({ ...log, type: 'personal_log' })));

    return items;
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Get travel icon
  const getTravelIcon = (travelType) => {
    switch (travelType) {
      case 'car':
        return <Car size={12} />;
      case 'plane':
        return <Plane size={12} />;
      case 'accommodation':
        return <Home size={12} />;
      default:
        return null;
    }
  };

  // Fetch project details
  const fetchProjectDetails = async (projectId) => {
    try {
      setLoadingProjectDetails(true);
      const token = localStorage.getItem('access_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_BASE_URL}/api/project/${projectId}/`, { headers });

      console.log('Project details from API:', response.data);
      setProjectDetails(response.data);
    } catch (error) {
      console.error('Error fetching project details:', error);
      setProjectDetails(null);
    } finally {
      setLoadingProjectDetails(false);
    }
  };

  // Open details modal
  const openDetailsModal = async (item) => {
    setSelectedItem(item);
    setShowDetailsModal(true);
    setProjectDetails(null);

    // If it's an assignment, fetch project details
    if (item.type === 'assignment' && item.project_id) {
      await fetchProjectDetails(item.project_id);
    }
  };

  // Render calendar
  const renderCalendar = () => {
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);
    const days = [];
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Week day headers
    const headers = weekDays.map((day) => (
      <div key={day} className="text-center text-xs font-bold text-slate-600 uppercase py-2 bg-steel-100">
        {day}
      </div>
    ));

    // Empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(
        <div key={`empty-${i}`} className="bg-slate-50 min-h-[100px] border-r border-b border-steel-200"></div>
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayItems = getItemsForDate(date);
      const hasItems = dayItems.length > 0;
      const isTodayDate = isToday(date);

      days.push(
        <div
          key={day}
          className={`min-h-[100px] border-r border-b border-steel-200 p-1 ${
            isTodayDate ? 'bg-brand-50' : 'bg-white hover:bg-slate-50'
          } transition-colors`}
        >
          <div className="flex items-center justify-between mb-1">
            <span className={`text-sm font-bold ${isTodayDate ? 'text-brand-600' : 'text-slate-700'}`}>
              {day}
            </span>
            {isTodayDate && (
              <span className="text-[10px] font-bold text-white bg-brand-500 px-1.5 py-0.5 rounded">Today</span>
            )}
          </div>
          {hasItems && (
            <div className="space-y-1">
              {dayItems.map((item, idx) => {
                if (item.type === 'assignment') {
                  const isSpecialTile = item.is_special_tile || item.isSpecialTile;
                  const displayName = isSpecialTile
                    ? (item.custom_name || item.custom_tile_name || item.project_name || 'Custom Task')
                    : item.project_name;

                  const customStyle = isSpecialTile && item.hex_color ? {
                    borderColor: item.hex_color,
                    backgroundColor: `${item.hex_color}15`,
                  } : {};

                  return (
                    <div
                      key={`assignment-${idx}`}
                      onClick={() => openDetailsModal(item)}
                      className={`p-1.5 rounded cursor-pointer text-[10px] ${
                        isSpecialTile
                          ? 'bg-purple-50 border-l-2 border-purple-400'
                          : 'bg-blue-50 border-l-2 border-blue-400'
                      }`}
                      title={displayName}
                      style={customStyle}
                    >
                      <div className="flex items-center gap-1">
                        <MapPin className="text-blue-500 flex-shrink-0" size={10} />
                        <span className="font-medium text-slate-700 truncate">{displayName}</span>
                      </div>
                      {item.travel_type && !isSpecialTile && (
                        <div className="flex items-center gap-1 mt-0.5 text-slate-500">
                          {getTravelIcon(item.travel_type)}
                          <span className="capitalize">{item.travel_type}</span>
                        </div>
                      )}
                    </div>
                  );
                } else if (item.type === 'travel') {
                  return (
                    <div
                      key={`travel-${idx}`}
                      onClick={() => openDetailsModal(item)}
                      className="p-1.5 rounded cursor-pointer text-[10px] bg-amber-50 border-l-2 border-amber-400"
                      title={`${item.travel_type === 'check_in' ? 'Check-In' : 'Check-Out'}`}
                    >
                      <div className="flex items-center gap-1">
                        <MapPin className="text-amber-500 flex-shrink-0" size={10} />
                        <span className="font-medium text-slate-700">
                          {item.travel_type === 'check_in' ? 'Check-In' : 'Check-Out'}
                        </span>
                        {item.transport_type && (
                          <span className="text-slate-500 capitalize">- {item.transport_type}</span>
                        )}
                      </div>
                    </div>
                  );
                } else if (item.type === 'holiday') {
                  const statusText = item.status === 'approved' ? 'Approved' :
                                   item.status === 'pending' ? 'Pending' :
                                   item.status === 'rejected' ? 'Rejected' : '';
                  const statusColors = item.status === 'approved'
                    ? 'bg-green-50 border-green-400 text-green-700'
                    : item.status === 'pending'
                      ? 'bg-yellow-50 border-yellow-400 text-yellow-700'
                      : 'bg-red-50 border-red-400 text-red-700';

                  return (
                    <div
                      key={`holiday-${idx}`}
                      onClick={() => openDetailsModal(item)}
                      className={`p-1.5 rounded cursor-pointer text-[10px] border-l-2 ${statusColors}`}
                      title={`Holiday - ${statusText}`}
                    >
                      <div className="flex items-center gap-1">
                        <Umbrella className="flex-shrink-0" size={10} />
                        <span className="font-medium truncate">Holiday - {statusText}</span>
                      </div>
                    </div>
                  );
                } else if (item.type === 'personal_log') {
                  return (
                    <div
                      key={`personal-log-${idx}`}
                      onClick={() => openDetailsModal(item)}
                      className="p-1.5 rounded cursor-pointer text-[10px] bg-sky-50 border-l-2 border-sky-400"
                      title={item.project_name}
                    >
                      <div className="flex items-center gap-1">
                        <CalendarCheck className="text-sky-500 flex-shrink-0" size={10} />
                        <span className="font-medium text-slate-700 truncate">{item.project_name}</span>
                      </div>
                      <div className="text-sky-600 mt-0.5">
                        {item.hours_from} - {item.hours_to}
                      </div>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="bg-white rounded-xl border border-steel-100 overflow-hidden">
        <div className="grid grid-cols-7 border-b-2 border-brand-500">{headers}</div>
        <div className="grid grid-cols-7">{days}</div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 p-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Planning Work</h1>
            {publishedDateRange && (
              <p className="text-sm text-slate-500 mt-1">
                Published plan: {new Date(publishedDateRange.start).toLocaleDateString()} - {new Date(publishedDateRange.end).toLocaleDateString()}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                const today = new Date().toISOString().split('T')[0];
                setPersonalLogForm({ date: today, project_name: '', hours_from: '', hours_to: '', notes: '' });
                setShowPersonalLogModal(true);
              }}
              className="bg-sky-500 text-white px-4 py-2 rounded-xl text-sm hover:bg-sky-600 transition-colors duration-300 flex items-center shadow-lg shadow-sky-500/20"
            >
              <Plus size={16} className="mr-2" />
              Add Work Log
            </button>
            <button
              onClick={() => setShowHolidayModal(true)}
              className="bg-emerald-500 text-white px-4 py-2 rounded-xl text-sm hover:bg-emerald-600 transition-colors duration-300 flex items-center shadow-lg shadow-emerald-500/20"
            >
              <Plus size={16} className="mr-2" />
              Add Holiday
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Navigation */}
      <div className="flex-shrink-0 px-4 pb-4">
        <div className="flex items-center justify-between bg-white rounded-xl border border-steel-100 p-3">
          <button
            onClick={goToPreviousMonth}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 transition-colors"
          >
            <ChevronLeft size={20} />
          </button>

          <div className="flex items-center gap-4">
            <h2 className="text-lg font-bold text-slate-900">
              {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h2>
            <button
              onClick={goToToday}
              className="px-3 py-1 text-sm font-medium text-brand-600 bg-brand-50 rounded-lg hover:bg-brand-100 transition-colors"
            >
              Today
            </button>
          </div>

          <button
            onClick={goToNextMonth}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 transition-colors"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>

      {/* Calendar */}
      <div className="flex-1 px-4 pb-4 overflow-auto">
        {assignments.length === 0 && travels.length === 0 && holidays.length === 0 && personalLogs.length === 0 ? (
          <div className="bg-white rounded-xl border border-steel-100 p-12 text-center">
            <CalendarCheck className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 font-medium">No work assignments, travel plans or holidays yet.</p>
            <p className="text-slate-400 text-sm mt-1">Click "Add Holiday" to add your first holiday request!</p>
          </div>
        ) : (
          <>
            {renderCalendar()}

            {/* Legend */}
            <div className="mt-4 bg-white rounded-xl border border-steel-100 p-4">
              <h3 className="text-sm font-bold text-slate-700 mb-3">Legend</h3>
              <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-3">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-blue-50 border-l-2 border-blue-400"></div>
                  <span className="text-xs text-slate-600">Assignment</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-amber-50 border-l-2 border-amber-400"></div>
                  <span className="text-xs text-slate-600">Check-In/Out</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-yellow-50 border-l-2 border-yellow-400"></div>
                  <span className="text-xs text-slate-600">Holiday (Pending)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-green-50 border-l-2 border-green-400"></div>
                  <span className="text-xs text-slate-600">Holiday (Approved)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-red-50 border-l-2 border-red-400"></div>
                  <span className="text-xs text-slate-600">Holiday (Rejected)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded bg-brand-50 border border-brand-200"></div>
                  <span className="text-xs text-slate-600">Today</span>
                </div>
                <div className="flex items-center gap-2">
                  <Car size={14} className="text-slate-500" />
                  <span className="text-xs text-slate-600">Car</span>
                </div>
                <div className="flex items-center gap-2">
                  <Plane size={14} className="text-slate-500" />
                  <span className="text-xs text-slate-600">Plane</span>
                </div>
                <div className="flex items-center gap-2">
                  <Home size={14} className="text-slate-500" />
                  <span className="text-xs text-slate-600">Accommodation</span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-brand-50 to-white px-6 py-4 border-b border-brand-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">
                {selectedItem.type === 'assignment' ? 'Project Details' :
                 selectedItem.type === 'holiday' ? 'Holiday Details' :
                 selectedItem.type === 'personal_log' ? 'Work Log Details' :
                 'Travel Details'}
              </h2>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedItem(null);
                  setProjectDetails(null);
                }}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

              {/* Modal Content */}
              <div className="p-6 overflow-y-auto flex-1 space-y-4">
                {selectedItem.type === 'assignment' ? (
                  // Assignment Details
                  <>
                    {/* Project/Task Name */}
                    <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                      <div className="flex items-center gap-3">
                        <MapPin className="text-blue-500" size={24} />
                        <div>
                          <p className="text-xs font-bold text-slate-500 uppercase">{selectedItem.is_special_tile ? 'Task Name' : 'Project Name'}</p>
                          <p className="text-lg font-bold text-slate-900">
                            {selectedItem.is_special_tile
                              ? (selectedItem.custom_name || selectedItem.custom_tile_name || selectedItem.project_name || 'Custom Task')
                              : (selectedItem.project_name || '-')
                            }
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Project Address - only show for regular projects, not custom tiles */}
                    {!selectedItem.is_special_tile && (
                      <div className="p-4 bg-slate-50 rounded-xl">
                        <p className="text-xs font-bold text-slate-500 uppercase mb-1">Project Address</p>
                        {loadingProjectDetails ? (
                          <div className="flex items-center gap-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-500"></div>
                            <span className="text-sm text-slate-500">Loading address...</span>
                          </div>
                        ) : (
                          <p className="text-sm text-slate-700">
                            {projectDetails ?
                              [
                                projectDetails.street_address,
                                projectDetails.postal_code,
                                projectDetails.city,
                                projectDetails.country
                              ].filter(Boolean).join(', ')
                              : '-'
                            }
                          </p>
                        )}
                      </div>
                    )}

                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-slate-50 rounded-xl">
                        <p className="text-xs font-bold text-slate-500 uppercase mb-1">Start Date</p>
                        <p className="text-sm font-medium text-slate-900">
                          {selectedItem.start_date ? new Date(selectedItem.start_date).toLocaleDateString('en-GB') : '-'}
                        </p>
                      </div>

                      <div className="p-4 bg-slate-50 rounded-xl">
                        <p className="text-xs font-bold text-slate-500 uppercase mb-1">End Date</p>
                        <p className="text-sm font-medium text-slate-900">
                          {selectedItem.end_date ? new Date(selectedItem.end_date).toLocaleDateString('en-GB') : '-'}
                        </p>
                      </div>
                    </div>

                    {/* Transport Type */}
                    {selectedItem.travel_type && (
                      <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                        <div className="flex items-center gap-3">
                          <div className="text-amber-500">
                            {getTravelIcon(selectedItem.travel_type)}
                          </div>
                          <div>
                            <p className="text-xs font-bold text-slate-500 uppercase">Transport Type</p>
                            <p className="text-sm font-medium text-slate-900 capitalize">{selectedItem.travel_type}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : selectedItem.type === 'travel' ? (
                  // Travel Details - clean compact design
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {/* Header */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      paddingBottom: '0.75rem',
                      borderBottom: '1px solid #e5e7eb'
                    }}>
                      <div>
                        <span style={{ fontSize: '1.125rem', fontWeight: 600, color: '#111827' }}>
                          {selectedItem.travel_type === 'check_in' ? 'Check-In' : 'Check-Out'}
                        </span>
                        <span style={{ marginLeft: '0.75rem', fontSize: '0.875rem', color: '#6b7280' }}>
                          {selectedItem.start_date ? new Date(selectedItem.start_date).toLocaleDateString('en-GB', {
                            weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
                          }) : '-'}
                        </span>
                      </div>
                      {(selectedItem.departure_time || selectedItem.arrival_time) && (
                        <div style={{ display: 'flex', gap: '1rem' }}>
                          {selectedItem.departure_time && (
                            <div style={{ textAlign: 'right' }}>
                              <span style={{ fontSize: '0.6875rem', color: '#9ca3af', textTransform: 'uppercase' }}>DEP </span>
                              <span style={{ fontSize: '1.125rem', fontWeight: 600, color: '#111827' }}>{selectedItem.departure_time}</span>
                            </div>
                          )}
                          {selectedItem.arrival_time && (
                            <div style={{ textAlign: 'right' }}>
                              <span style={{ fontSize: '0.6875rem', color: '#9ca3af', textTransform: 'uppercase' }}>ARR </span>
                              <span style={{ fontSize: '1.125rem', fontWeight: 600, color: '#111827' }}>{selectedItem.arrival_time}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Content Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      {/* Left Column */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {/* Flight */}
                        {selectedItem.transport_type === 'plane' && (selectedItem.departure_airport || selectedItem.arrival_airport) && (
                          <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '6px' }}>
                            <p style={{ margin: '0 0 0.5rem', fontSize: '0.6875rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Flight</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span style={{ fontSize: '1rem', fontWeight: 600, color: '#111827' }}>{selectedItem.departure_airport || '—'}</span>
                              <span style={{ color: '#9ca3af' }}>→</span>
                              <span style={{ fontSize: '1rem', fontWeight: 600, color: '#111827' }}>{selectedItem.arrival_airport || '—'}</span>
                              {selectedItem.flight_number && (
                                <span style={{ marginLeft: 'auto', fontSize: '0.8125rem', color: '#6b7280' }}>{selectedItem.flight_number}</span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Car */}
                        {selectedItem.transport_type === 'car' && (
                          <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '6px' }}>
                            <p style={{ margin: '0 0 0.25rem', fontSize: '0.6875rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Car</p>
                            {selectedItem.car ? (
                              <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 500, color: '#111827' }}>
                                {selectedItem.car.car_brand} {selectedItem.car.car_model} <span style={{ color: '#6b7280' }}>({selectedItem.car.plate_number})</span>
                              </p>
                            ) : selectedItem.own_car ? (
                              <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>Own Car</p>
                            ) : (
                              <p style={{ margin: 0, fontSize: '0.875rem', color: '#6b7280' }}>Car assigned</p>
                            )}
                          </div>
                        )}

                        {/* Organized Transport */}
                        {selectedItem.organized_transport && (
                          <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '6px' }}>
                            <p style={{ margin: '0 0 0.5rem', fontSize: '0.6875rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Organized Transport</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.875rem' }}>
                              {selectedItem.transport_company && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span style={{ color: '#6b7280' }}>Company</span>
                                  <span style={{ fontWeight: 500, color: '#111827' }}>{selectedItem.transport_company}</span>
                                </div>
                              )}
                              {selectedItem.meeting_time && (
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                  <span style={{ color: '#6b7280' }}>Meeting</span>
                                  <span style={{ fontWeight: 500, color: '#111827' }}>{selectedItem.meeting_time}</span>
                                </div>
                              )}
                              {selectedItem.meeting_point && (
                                <div><span style={{ color: '#6b7280' }}>Pickup: </span><span style={{ color: '#111827' }}>{selectedItem.meeting_point}</span></div>
                              )}
                              {selectedItem.dropoff_location && (
                                <div><span style={{ color: '#6b7280' }}>Dropoff: </span><span style={{ color: '#111827' }}>{selectedItem.dropoff_location}</span></div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Destination */}
                        {selectedItem.address && (
                          <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '6px' }}>
                            <p style={{ margin: '0 0 0.25rem', fontSize: '0.6875rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Destination</p>
                            <p style={{ margin: 0, fontSize: '0.875rem', color: '#111827' }}>{selectedItem.address}</p>
                          </div>
                        )}
                      </div>

                      {/* Right Column */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {/* Driver */}
                        {selectedItem.pickup_driver_data && (
                          <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '6px' }}>
                            <p style={{ margin: '0 0 0.25rem', fontSize: '0.6875rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Your Driver</p>
                            <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 500, color: '#111827' }}>
                              {selectedItem.pickup_driver_data.user_name || selectedItem.pickup_driver_data.first_name + ' ' + selectedItem.pickup_driver_data.last_name}
                              {selectedItem.pickup_driver_data.phone_number && (
                                <span style={{ fontWeight: 400, color: '#6b7280' }}> • {selectedItem.pickup_driver_data.phone_number}</span>
                              )}
                            </p>
                          </div>
                        )}

                        {/* Passengers */}
                        {selectedItem.passengers_data && selectedItem.passengers_data.length > 0 && (
                          <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '6px' }}>
                            <p style={{ margin: '0 0 0.5rem', fontSize: '0.6875rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
                              Passengers ({selectedItem.passengers_data.length})
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.875rem' }}>
                              {selectedItem.passengers_data.map((p, idx) => (
                                <div key={idx}>
                                  <span style={{ fontWeight: 500, color: '#111827' }}>
                                    {p.user_name || p.first_name + ' ' + p.last_name}
                                  </span>
                                  {p.phone_number && <span style={{ color: '#6b7280' }}> • {p.phone_number}</span>}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Notes */}
                        {(selectedItem.notes || selectedItem.description || selectedItem.planner_note) && (
                          <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '6px' }}>
                            <p style={{ margin: '0 0 0.25rem', fontSize: '0.6875rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>Notes</p>
                            <p style={{ margin: 0, fontSize: '0.875rem', color: '#111827', whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                              {selectedItem.notes || selectedItem.description || selectedItem.planner_note}
                            </p>
                          </div>
                        )}

                        {/* Empty state */}
                        {!selectedItem.pickup_driver_data &&
                         !(selectedItem.passengers_data && selectedItem.passengers_data.length > 0) &&
                         !selectedItem.notes && !selectedItem.description && !selectedItem.planner_note && (
                          <div style={{ padding: '1rem', textAlign: 'center' }}>
                            <p style={{ margin: 0, fontSize: '0.875rem', color: '#9ca3af' }}>No additional details</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : selectedItem.type === 'holiday' ? (
                  // Holiday Details
                  <>
                    {/* Holiday Badge */}
                    <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                      <div className="flex items-center gap-3">
                        <Umbrella className="text-amber-500" size={24} />
                        <div>
                          <p className="text-xs font-bold text-slate-500 uppercase">Holiday Request</p>
                          <p className="text-lg font-bold text-amber-600">Vacation Time</p>
                        </div>
                      </div>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-slate-50 rounded-xl">
                        <p className="text-xs font-bold text-slate-500 uppercase mb-1">Start Date</p>
                        <p className="text-sm font-medium text-slate-900">
                          {selectedItem.start_date ? new Date(selectedItem.start_date).toLocaleDateString('en-GB') : '-'}
                        </p>
                      </div>

                      <div className="p-4 bg-slate-50 rounded-xl">
                        <p className="text-xs font-bold text-slate-500 uppercase mb-1">End Date</p>
                        <p className="text-sm font-medium text-slate-900">
                          {selectedItem.end_date ? new Date(selectedItem.end_date).toLocaleDateString('en-GB') : '-'}
                        </p>
                      </div>
                    </div>

                    {/* Duration */}
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <p className="text-xs font-bold text-slate-500 uppercase mb-1">Duration</p>
                      <p className="text-sm font-medium text-slate-900">
                        {selectedItem.start_date && selectedItem.end_date ?
                          Math.ceil((new Date(selectedItem.end_date) - new Date(selectedItem.start_date)) / (1000 * 60 * 60 * 24)) + 1
                          : '-'} days
                      </p>
                    </div>

                    {/* Notes */}
                    {selectedItem.notes && (
                      <div className="p-4 bg-slate-50 rounded-xl">
                        <p className="text-xs font-bold text-slate-500 uppercase mb-1">Notes</p>
                        <p className="text-sm text-slate-700">{selectedItem.notes}</p>
                      </div>
                    )}
                  </>
                ) : selectedItem.type === 'personal_log' ? (
                  // Personal Log Details
                  <>
                    {/* Personal Log Badge */}
                    <div className="p-4 bg-sky-50 rounded-xl border border-sky-200">
                      <div className="flex items-center gap-3">
                        <CalendarCheck className="text-sky-500" size={24} />
                        <div>
                          <p className="text-xs font-bold text-slate-500 uppercase">Personal Work Log</p>
                          <p className="text-lg font-bold text-sky-700">
                            {selectedItem.project_name}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Date */}
                    <div className="p-4 bg-slate-50 rounded-xl">
                      <p className="text-xs font-bold text-slate-500 uppercase mb-1">Date</p>
                      <p className="text-sm font-medium text-slate-900">
                        {selectedItem.date ? new Date(selectedItem.date).toLocaleDateString('en-GB') : '-'}
                      </p>
                    </div>

                    {/* Hours */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-slate-50 rounded-xl">
                        <p className="text-xs font-bold text-slate-500 uppercase mb-1">Start Time</p>
                        <p className="text-sm font-medium text-slate-900">
                          {selectedItem.hours_from || '-'}
                        </p>
                      </div>

                      <div className="p-4 bg-slate-50 rounded-xl">
                        <p className="text-xs font-bold text-slate-500 uppercase mb-1">End Time</p>
                        <p className="text-sm font-medium text-slate-900">
                          {selectedItem.hours_to || '-'}
                        </p>
                      </div>
                    </div>

                    {/* Notes */}
                    {selectedItem.notes && (
                      <div className="p-4 bg-slate-50 rounded-xl">
                        <p className="text-xs font-bold text-slate-500 uppercase mb-1">Notes</p>
                        <p className="text-sm text-slate-700">{selectedItem.notes}</p>
                      </div>
                    )}
                  </>
                ) : null}
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end px-6 py-4 border-t border-slate-200">
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setSelectedItem(null);
                    setProjectDetails(null);
                  }}
                  className="px-4 py-2 text-sm font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Close
                </button>
              </div>
          </div>
        </div>
      )}

      {/* Holiday Form Modal */}
      {showHolidayModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-emerald-50 to-white px-6 py-4 border-b border-emerald-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">Request Holiday</h2>
              <button
                onClick={() => {
                  setShowHolidayModal(false);
                  setHolidayForm({ start_date: '', end_date: '', notes: '' });
                }}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <form onSubmit={async (e) => {
                e.preventDefault();
                setSavingHoliday(true);

                try {
                  const token = localStorage.getItem('access_token');
                  const headers = token ? { Authorization: `Bearer ${token}` } : {};

                  // Don't send user_id - it's set automatically by the backend from the authenticated user
                  const holidayData = {
                    ...holidayForm,
                    holiday_type: 'H'  // Default to 'H' (Holiday)
                  };

                  console.log('Submitting holiday data:', holidayData);

                  const response = await axios.post(`${API_BASE_URL}/api/holidays/`, holidayData, { headers });
                  console.log('Holiday created:', response.data);

                  // Refresh holidays list
                  await fetchHolidays();

                  // Close modal and reset form
                  setShowHolidayModal(false);
                  setHolidayForm({ start_date: '', end_date: '', notes: '' });

                  alert('Holiday request submitted successfully!');
                } catch (error) {
                  console.error('Error saving holiday:', error);
                  console.error('Error response:', error.response?.data);
                  console.error('Error status:', error.response?.status);

                  const errorMessage = error.response?.data?.detail
                    || error.response?.data?.error
                    || JSON.stringify(error.response?.data)
                    || 'Error submitting holiday request. Please try again.';

                  alert(`Error: ${errorMessage}`);
                } finally {
                  setSavingHoliday(false);
                }
              }}>
                {/* Start and End Date - First Row */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2" htmlFor="holiday-start-date">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="holiday-start-date"
                      type="date"
                      required
                      value={holidayForm.start_date}
                      onChange={(e) => setHolidayForm({ ...holidayForm, start_date: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2" htmlFor="holiday-end-date">
                      End Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="holiday-end-date"
                      type="date"
                      required
                      value={holidayForm.end_date}
                      onChange={(e) => setHolidayForm({ ...holidayForm, end_date: e.target.value })}
                      min={holidayForm.start_date}
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                </div>

                {/* Duration Display */}
                {holidayForm.start_date && holidayForm.end_date && (
                  <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-lg mb-4 text-center">
                    <p className="text-xs font-medium text-emerald-600 mb-1">Duration</p>
                    <p className="text-xl font-bold text-emerald-600">
                      {Math.ceil((new Date(holidayForm.end_date) - new Date(holidayForm.start_date)) / (1000 * 60 * 60 * 24)) + 1} days
                    </p>
                  </div>
                )}

                {/* Notes */}
                <div className="mb-4">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-2" htmlFor="holiday-notes">
                    Notes (Optional)
                  </label>
                  <textarea
                    id="holiday-notes"
                    value={holidayForm.notes}
                    onChange={(e) => setHolidayForm({ ...holidayForm, notes: e.target.value })}
                    placeholder="Add any additional notes..."
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                  />
                </div>

                {/* Modal Footer */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowHolidayModal(false);
                      setHolidayForm({ start_date: '', end_date: '', notes: '' });
                    }}
                    className="px-4 py-2 text-sm font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                    disabled={savingHoliday}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-bold text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-50 shadow-lg shadow-emerald-500/20"
                    disabled={savingHoliday}
                  >
                    {savingHoliday ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Personal Work Log Modal */}
      {showPersonalLogModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-sky-50 to-white px-6 py-4 border-b border-sky-100 flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900">Add Personal Work Log</h2>
              <button
                type="button"
                onClick={() => {
                  setShowPersonalLogModal(false);
                  setPersonalLogForm({ date: '', project_name: '', hours_from: '', hours_to: '', notes: '' });
                }}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <form onSubmit={async (e) => {
                e.preventDefault();
                setSavingPersonalLog(true);

                try {
                  const token = localStorage.getItem('access_token');
                  const headers = token ? { Authorization: `Bearer ${token}` } : {};

                  const logData = {
                    ...personalLogForm
                  };

                  console.log('Submitting personal log:', logData);

                  const response = await axios.post(`${API_BASE_URL}/api/personal-work-logs/`, logData, { headers });
                  console.log('Personal log created:', response.data);

                  // Refresh personal logs list
                  await fetchPersonalLogs();

                  // Close modal and reset form
                  setShowPersonalLogModal(false);
                  setPersonalLogForm({ date: '', project_name: '', hours_from: '', hours_to: '', notes: '' });

                  alert('Work log added successfully!');
                } catch (error) {
                  console.error('Error saving personal log:', error);
                  console.error('Error response:', error.response?.data);

                  const errorMessage = error.response?.data?.detail
                    || error.response?.data?.error
                    || JSON.stringify(error.response?.data)
                    || 'Error adding work log. Please try again.';

                  alert(`Error: ${errorMessage}`);
                } finally {
                  setSavingPersonalLog(false);
                }
              }}>
                {/* Date and Project - First Row */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2" htmlFor="log-date">
                      Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="log-date"
                      type="date"
                      required
                      value={personalLogForm.date}
                      onChange={(e) => setPersonalLogForm({ ...personalLogForm, date: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2" htmlFor="log-project">
                      Project / Activity <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="log-project"
                      type="text"
                      required
                      value={personalLogForm.project_name}
                      onChange={(e) => setPersonalLogForm({ ...personalLogForm, project_name: e.target.value })}
                      placeholder="e.g., Admin work, Training"
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    />
                  </div>
                </div>

                {/* Hours - Second Row */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2" htmlFor="log-hours-from">
                      Start Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="log-hours-from"
                      type="time"
                      required
                      value={personalLogForm.hours_from}
                      onChange={(e) => setPersonalLogForm({ ...personalLogForm, hours_from: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2" htmlFor="log-hours-to">
                      End Time <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="log-hours-to"
                      type="time"
                      required
                      value={personalLogForm.hours_to}
                      onChange={(e) => setPersonalLogForm({ ...personalLogForm, hours_to: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    />
                  </div>
                </div>

                {/* Notes - Third Row */}
                <div className="mb-4">
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-2" htmlFor="log-notes">
                    Notes (Optional)
                  </label>
                  <textarea
                    id="log-notes"
                    value={personalLogForm.notes}
                    onChange={(e) => setPersonalLogForm({ ...personalLogForm, notes: e.target.value })}
                    placeholder="Add any additional details..."
                    rows={2}
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 resize-none"
                  />
                </div>

                {/* Modal Footer */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowPersonalLogModal(false);
                      setPersonalLogForm({ date: '', project_name: '', hours_from: '', hours_to: '', notes: '' });
                    }}
                    className="px-4 py-2 text-sm font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                    disabled={savingPersonalLog}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-bold text-white bg-sky-500 rounded-lg hover:bg-sky-600 transition-colors disabled:opacity-50 shadow-lg shadow-sky-500/20"
                    disabled={savingPersonalLog}
                  >
                    {savingPersonalLog ? 'Saving...' : 'Save Log'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanningWork;
